import {useMessage} from '@coolcolduk/ui-mui';
import {TradeDirection, TradeStatus, type Base64ObjectInput, type Trade} from '@cp/model';
import BondImageUpload from '../../../bonds/bondImageUpload/BondImageUpload';
import {Add, ExpandMore} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {useState} from 'react';
import {createTrade} from '../../../api/trade/createTrade';
import uploadStorageBase64 from '../../../helper/firebase/storage/uploadStorageBase64';
import {getCurrentUserIdValidated} from '../../../config/firebase';

interface AdminTradeCreateFormProps {
  onTradeCreated: (trade: Trade) => void;
}

export default function AdminTradeCreateForm({onTradeCreated}: AdminTradeCreateFormProps) {
  const {showMessage} = useMessage();

  const [creating, setCreating] = useState(false);
  const [newSymbol, setNewSymbol] = useState('');
  const [newSessionId, setNewSessionId] = useState('');
  const [newDirection, setNewDirection] = useState<TradeDirection>(TradeDirection.LONG);
  const [newStatus, setNewStatus] = useState<TradeStatus>(TradeStatus.CLOSED);
  const [newEntryPrice, setNewEntryPrice] = useState<string>('');
  const [newExitPrice, setNewExitPrice] = useState<string>('');
  const [newTags, setNewTags] = useState('');
  const [imageOption, setImageOption] = useState<'none' | 'image'>('none');
  const [messageScreenshot, setMessageScreenshot] = useState<Base64ObjectInput | null>(null);

  const parseTags = (input: string): string[] =>
    input
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t.length > 0);

  const handleCreate = async () => {
    const entryPrice = Number(newEntryPrice);
    const rawExitPrice = newExitPrice.trim() === '' ? undefined : Number(newExitPrice);
    const exitPrice = newStatus === TradeStatus.OPEN ? undefined : rawExitPrice;
    const tags = parseTags(newTags);

    if (!newSymbol.trim() || !newSessionId.trim()) {
      showMessage('Symbol and sessionId are required', 4000, 'error');
      return;
    }

    if (!Number.isFinite(entryPrice) || Number.isNaN(entryPrice)) {
      showMessage('Entry (open) price is required and must be a number', 4000, 'error');
      return;
    }

    if (newStatus !== TradeStatus.OPEN && (exitPrice == null || !Number.isFinite(exitPrice))) {
      showMessage('Exit (close) price is required for closed trades', 4000, 'error');
      return;
    }

    if (imageOption === 'image' && messageScreenshot == null) {
      showMessage('Please upload or paste an image', 4000, 'error');
      return;
    }

    setCreating(true);
    try {
      const uid = await getCurrentUserIdValidated();
      const createdAtMs = Date.now();

      let screenshotStoragePath: string | undefined;
      if (imageOption === 'image' && messageScreenshot != null) {
        const extFromType = messageScreenshot.type.split('/')[1] ?? 'png';
        const ext = extFromType.trim() !== '' ? extFromType : 'png';
        screenshotStoragePath = `private/${uid}/trades/manual/${createdAtMs}.${ext}`;

        await uploadStorageBase64(messageScreenshot, screenshotStoragePath);
      }

      const created = await createTrade({
        symbol: newSymbol.trim(),
        sessionId: newSessionId.trim(),
        direction: newDirection,
        entryPrice,
        exitPrice,
        status: newStatus,
        tags,
        screenshotStoragePath: screenshotStoragePath ?? undefined,
      });

      onTradeCreated(created);

      setNewSymbol('');
      setNewSessionId('');
      setNewDirection(TradeDirection.LONG);
      setNewStatus(TradeStatus.CLOSED);
      setNewEntryPrice('');
      setNewExitPrice('');
      setNewTags('');
      setImageOption('none');
      setMessageScreenshot(null);

      showMessage('Trade created', 4000, 'success');
    } catch (error) {
      console.error('Failed to create trade', error);
      showMessage('Failed to create trade', 6000, 'error');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Accordion sx={{mb: 3}} defaultExpanded={false}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography variant="subtitle1">Create trade (admin only)</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack direction="column" spacing={2}>
          <TextField
            label="Symbol"
            size="small"
            value={newSymbol}
            onChange={(e) => setNewSymbol(e.target.value)}
            sx={{minWidth: {xs: '100%', sm: 160}}}
          />

          <TextField
            label="Session ID (manual)"
            size="small"
            value={newSessionId}
            onChange={(e) => setNewSessionId(e.target.value)}
            sx={{minWidth: {xs: '100%', sm: 220}}}
          />

          <FormControl size="small" sx={{minWidth: 180}}>
            <InputLabel id="new-direction-label">Direction</InputLabel>
            <Select
              labelId="new-direction-label"
              value={newDirection}
              label="Direction"
              onChange={(e) => setNewDirection(e.target.value as TradeDirection)}
            >
              <MenuItem value={TradeDirection.LONG}>Long</MenuItem>
              <MenuItem value={TradeDirection.SHORT}>Short</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{minWidth: 200}}>
            <InputLabel id="new-status-label">Close type</InputLabel>
            <Select
              labelId="new-status-label"
              value={newStatus}
              label="Close type"
              onChange={(e) => setNewStatus(e.target.value as TradeStatus)}
            >
              <MenuItem value={TradeStatus.OPEN}>Open</MenuItem>
              <MenuItem value={TradeStatus.CLOSED}>Closed (manual)</MenuItem>
              <MenuItem value={TradeStatus.STOPLOSS}>Closed (stop loss)</MenuItem>
              <MenuItem value={TradeStatus.TAKEPROFIT}>Closed (take profit)</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Entry (open) price"
            size="small"
            type="number"
            value={newEntryPrice}
            onChange={(e) => setNewEntryPrice(e.target.value)}
            sx={{minWidth: {xs: '100%', sm: 220}}}
          />

          <TextField
            label="Exit (close) price"
            size="small"
            type="number"
            value={newExitPrice}
            onChange={(e) => setNewExitPrice(e.target.value)}
            disabled={newStatus === TradeStatus.OPEN}
            sx={{minWidth: {xs: '100%', sm: 220}}}
          />

          <TextField
            label="Tags (comma separated)"
            size="small"
            value={newTags}
            onChange={(e) => setNewTags(e.target.value)}
            sx={{minWidth: {xs: '100%', sm: 320}}}
          />

          <FormControl size="small" sx={{minWidth: {xs: '100%', sm: 320}}}>
            <InputLabel id="new-message-image-option-label">Message image</InputLabel>
            <Select
              labelId="new-message-image-option-label"
              value={imageOption}
              label="Message image"
              onChange={(e) => {
                const next = e.target.value as 'none' | 'image';
                setImageOption(next);
                if (next === 'none') setMessageScreenshot(null);
              }}
            >
              <MenuItem value="none">None</MenuItem>
              <MenuItem value="image">Add image (upload or paste)</MenuItem>
            </Select>
          </FormControl>

          {imageOption === 'image' && (
            <BondImageUpload
              label="Optional image for the trade message"
              currentImageUrl={null}
              onImageChange={(image) => setMessageScreenshot(image)}
            />
          )}

          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreate}
            disabled={creating}
            sx={{textTransform: 'none', alignSelf: {xs: 'stretch', sm: 'center'}}}
          >
            Create trade
          </Button>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
